/**
 * Centralized Error Handling System
 * 
 * 3 Layers:
 * 1. AUTH - Login/session errors → logout + redirect
 * 2. ACTION - User action errors → specific toast messages
 * 3. BACKGROUND - Silent errors → console only, fallback UI
 */

import { toast } from "sonner";

/**
 * Flatten an axios/fetch error into a safe string suitable for `toast.error()`,
 * `<Alert>` content, or any other React child slot.
 *
 * As of the 10 Jul 2026 refactor, the Axios response interceptor in `lib/api.js`
 * ALREADY normalises every API error into a `NormalizedApiError` whose
 * `.message` is a guaranteed-safe string. New code should just do:
 *
 *     try { … } catch (err) { toast.error(err.message); }
 *
 * This helper remains as:
 *   1. A backward-compat shim for the ~70 catch-blocks that pass a caller-
 *      specific fallback message: `extractErrorMessage(err, "Failed to save")`.
 *   2. A safety net for the rare non-axios exception path (thrown strings,
 *      synchronous validation errors, third-party SDK errors, etc.) where
 *      the interceptor never got to run.
 *
 * Precedence:
 *   • If `error` is already normalised → return `error.message || fallback`.
 *   • Otherwise → run the full extraction pipeline defensively.
 *
 * @param {*} error       Any thrown value (NormalizedApiError, axios error,
 *                        string, generic Error, or non-Error object).
 * @param {string} fallback  Fallback string when nothing extractable is found.
 * @returns {string}      Always a safe, ≤ 400-char string.
 */
export function extractErrorMessage(error, fallback = "Something went wrong") {
  if (!error) return fallback;

  // Fast path: interceptor-normalised NormalizedApiError. `error.message` is
  // guaranteed non-object, non-null, human-readable. We still respect the
  // caller-provided `fallback` for the (rare) case where message is empty.
  if (error?.name === "NormalizedApiError") {
    return error.message || fallback;
  }

  const raw =
    error?.response?.data?.detail ??
    error?.response?.data?.message ??
    error?.response?.data?.error ??
    error?.data?.detail ??
    error?.data?.message ??
    error?.message ??
    error;

  if (raw == null) return fallback;
  if (typeof raw === "string") return raw.slice(0, 400) || fallback;

  if (Array.isArray(raw)) {
    // Pydantic 422 → array of {type, loc, msg, input}
    const parts = raw
      .map((e) => {
        if (typeof e === "string") return e;
        if (!e || typeof e !== "object") return String(e);
        const loc = Array.isArray(e.loc) ? e.loc.slice(-2).join(".") : "";
        const msg = e.msg || e.message || "Invalid input";
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean);
    return (parts.join("; ").slice(0, 400)) || fallback;
  }

  if (typeof raw === "object") {
    // Sometimes the API returns { detail: { msg, code, ... } } (single obj).
    if (raw.msg || raw.message) return String(raw.msg || raw.message).slice(0, 400);
    try {
      return JSON.stringify(raw).slice(0, 400);
    } catch {
      return fallback;
    }
  }

  return String(raw).slice(0, 400) || fallback;
}

// Error types
export const ErrorType = {
  AUTH: "auth",
  ACTION: "action", 
  BACKGROUND: "background"
};

// Specific error messages for actions
const ACTION_MESSAGES = {
  // Production actions
  "record-receipt": "Failed to record receipt",
  "add-stage": "Failed to add stage",
  "dispatch": "Failed to record dispatch",
  "declare-loss": "Failed to declare loss",
  "cancel-lot": "Failed to cancel lot",
  "assign-lot": "Failed to assign lot number",
  "mark-ready": "Failed to mark lot as ready",
  "create-lot": "Failed to create production lot",
  
  // Stock actions
  "create-stock": "Failed to create stock",
  "update-stock": "Failed to update stock",
  "delete-stock": "Failed to delete",
  "upload": "Failed to upload file",
  "import": "Failed to import data",
  "export": "Failed to export data",
  
  // Generic
  "save": "Failed to save changes",
  "load": "Failed to load data",
  "update": "Failed to update",
  "delete": "Failed to delete",
};

/**
 * Handle API errors based on type
 * 
 * @param {Error} error - The error object
 * @param {string} type - Error type: "auth", "action", "background"
 * @param {string} actionKey - Key for specific action message (optional)
 * @returns {string|null} - Error message or null for background errors
 */
export function handleApiError(error, type, actionKey = null) {
  const status = error.response?.status;
  // Use the shared extractor so Pydantic 422 arrays never crash callers.
  const serverMessage = extractErrorMessage(error, "");
  
  // AUTH ERRORS
  if (type === ErrorType.AUTH) {
    if (status === 401 || status === 403) {
      toast.error("Invalid email or password");
    } else if (status === 404) {
      toast.error("Account not found");
    } else if (!error.response) {
      toast.error("Network error. Please check your connection.");
    } else {
      toast.error(serverMessage || "Login failed");
    }
    return serverMessage || "Login failed";
  }
  
  // ACTION ERRORS
  if (type === ErrorType.ACTION) {
    const specificMessage = actionKey ? ACTION_MESSAGES[actionKey] : null;
    const displayMessage = serverMessage || specificMessage || "Action failed";
    
    toast.error(displayMessage);
    
    return displayMessage;
  }
  
  // BACKGROUND ERRORS (Silent)
  if (type === ErrorType.BACKGROUND) {
    // Silent - no toast, no log
    return null;
  }
  
  // Unknown type - silent
  return null;
}

/**
 * Wrapper for async API calls with error handling
 * 
 * @param {Function} apiCall - The API call function
 * @param {string} type - Error type
 * @param {string} actionKey - Action key for specific message
 * @returns {Promise} - Resolves with data or rejects with handled error
 */
export async function safeApiCall(apiCall, type = ErrorType.ACTION, actionKey = null) {
  try {
    const response = await apiCall();
    return response;
  } catch (error) {
    handleApiError(error, type, actionKey);
    throw error;
  }
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error) {
  return !error.response && error.message === "Network Error";
}

/**
 * Check if error is unauthorized
 */
export function isUnauthorized(error) {
  return error.response?.status === 401;
}
